import React, { useState } from 'react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { useQuery } from 'react-query';
import { getInstagramAccount } from '../lib/instagram';

interface Message {
  id: string;
  content: string;
  sender: string;
  timestamp: string;
  isInternal?: boolean;
}

interface ChatWindowProps {
  messages: Message[];
  username: string;
  isDisabled?: boolean;
  isInfluencer?: boolean;
  avatar?: string;
}

export default function ChatWindow({ messages, username, isDisabled, isInfluencer, avatar }: ChatWindowProps) {
  const [newMessage, setNewMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const { data: account } = useQuery('instagramAccount', getInstagramAccount);
  const [showSaveToLibrary, setShowSaveToLibrary] = useState(false);
  const [showAssignMenu, setShowAssignMenu] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;

    if (!account?.connected) {
      toast.error('Please connect your Instagram account first');
      return;
    }

    // Handle message sending logic here
    setNewMessage('');
  };

  const handleSaveToLibrary = () => {
    if (isDisabled) {
      toast.error('Please connect your Instagram account first');
      return;
    }
    setShowSaveToLibrary(true);
  };

  const handleAssign = (teamMember: string) => {
    if (isDisabled) {
      toast.error('Please connect your Instagram account first');
      return;
    }
    toast.success(`Conversation assigned to ${teamMember}`);
    setShowAssignMenu(false);
  };

  const teamMembers = [
    'Sarah Johnson',
    'Mike Chen',
    'Alex Rodriguez',
    'Emma Wilson'
  ];

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-sm">
      {/* Chat header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {isInfluencer ? (
              <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-purple-600 to-pink-500 p-2">
                <svg viewBox="0 0 24 24" fill="white">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </div>
            ) : (
              <img
                src={avatar}
                alt=""
                className="w-10 h-10 rounded-full bg-gray-200 object-cover"
              />
            )}
            <div>
              <h2 className="text-lg font-medium text-gray-900">@{username}</h2>
              <p className="text-sm text-gray-500">Instagram Direct Message</p>
            </div>
          </div>

          {/* Assign button */}
          <div className="relative">
            <button
              onClick={() => setShowAssignMenu(!showAssignMenu)}
              disabled={isDisabled}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="h-5 w-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Assign
            </button>

            {/* Assign menu */}
            {showAssignMenu && (
              <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1">
                  {teamMembers.map((member) => (
                    <button
                      key={member}
                      onClick={() => handleAssign(member)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {member}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.sender === 'me' ? 'justify-end' : 'justify-start'
            }`}
          >
            <div
              className={`max-w-[70%] ${
                message.isInternal
                  ? 'bg-purple-50 border border-purple-100'
                  : message.sender === 'me'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-900'
              } rounded-lg px-4 py-2`}
            >
              {message.isInternal && (
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-xs font-medium text-purple-600">Internal Note</span>
                </div>
              )}
              <p className="text-sm">{message.content}</p>
              <div
                className={`text-xs mt-1 ${
                  message.sender === 'me' ? 'text-white/70' : 'text-gray-500'
                }`}
              >
                {format(new Date(message.timestamp), 'h:mm a')}
              </div>
            </div>
          </div>
        ))}

        {isDisabled && (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <svg
              className="h-12 w-12 text-gray-400 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900">Connect Instagram to Start Messaging</h3>
            <p className="text-red-400 mt-2">
              Please connect your Instagram account to send a DM to this user!
            </p>
          </div>
        )}
      </div>

      {/* Message input */}
      <div className="p-4 border-t border-gray-200">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={isInternal}
                onChange={(e) => setIsInternal(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                disabled={isDisabled}
              />
              <span className="text-sm text-gray-700">Internal comment</span>
            </label>
            <button
              type="button"
              onClick={handleSaveToLibrary}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium disabled:opacity-50"
              disabled={isDisabled}
            >
              Save to Library
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={isDisabled ? "Please connect your Instagram account!" : isInternal ? "Add an internal note..." : "Type your message..."}
              className={`flex-1 rounded-lg border-gray-300 focus:ring-primary-500 focus:border-primary-500 py-3 px-4 disabled:bg-gray-50 ${
                isDisabled ? 'text-red-400 placeholder-red-400' : 'text-gray-900'
              }`}
              disabled={isDisabled}
            />
            <button
              type="submit"
              disabled={isDisabled}
              className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Send
            </button>
          </div>
        </form>
      </div>

      {/* Save to Library Modal */}
      {showSaveToLibrary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Save to Library</h3>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Collection name..."
                className="w-full rounded-lg border-gray-300 focus:ring-primary-500 focus:border-primary-500"
              />
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowSaveToLibrary(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    toast.success('Saved to library');
                    setShowSaveToLibrary(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}